import { useState, useCallback } from "react";
import { hasSufficientBlessBalance } from "@/utils/check-bless-balance.util";
import { BLESSNET_SAFETY_CONFIG } from "@/config/blessnet-safety.config";
import { getTokenGatewayAddresses } from "@/features/bridge/services/api/getTokenGatewayAddresses";
import { BLESSNET_CHAIN_ID } from "@/features/bridge/constants/bridge.constant";
import { CONTRACT_ADDRESSES } from "@/constants/contract-addresses.constants";
import type { HexString } from "@/types/general.types";
import type { ChainData } from "@/constants/chain-details";

/**
 * Hook to handle Blessnet bridging validation and safety checks
 */
export const useBlessnetValidation = () => {
  const [isValidating, setIsValidating] = useState(false);
  const [showUnavailableModal, setShowUnavailableModal] = useState(false);

  /**
   * Checks if bridging to Blessnet is allowed based on the target contract's BLESS balance
   */
  const validateBlessnetBridging = useCallback(async (
    destinationChain: ChainData | undefined,
    tokenAddress?: HexString,
    fallbackContractAddress?: HexString,
    originChainId?: number
  ): Promise<boolean> => {
    // Only validate if we're bridging to Blessnet
    if (destinationChain?.chainId !== BLESSNET_CHAIN_ID) {
      console.log('🔄 [BLESSNET SAFETY] Not bridging to Blessnet - skipping validation');
      return true; // Allow non-Blessnet bridging
    }

    // Skip validation when bridging BLESS tokens themselves
    if (tokenAddress && tokenAddress.toLowerCase() === CONTRACT_ADDRESSES.BLESS_TOKEN.toLowerCase()) {
      console.log('💰 [BLESSNET SAFETY] Bridging BLESS token itself - skipping balance validation');
      return true; // Allow BLESS token bridging without balance check
    }

    setIsValidating(true);

    try {
      // Determine which address to check based on token type
      let addressToCheck: HexString;
      let checkReason: string;

      // Fetch self-bridging token data when needed
      let isSelfBridging = false;
      if (tokenAddress && originChainId) {
        try {
          const response = await getTokenGatewayAddresses(originChainId);
          const selfBridgingAddresses = response.watchedAddresses
            ?.filter(addr => addr.isActive)
            ?.map(addr => addr.address.toLowerCase()) || [];

          isSelfBridging = selfBridgingAddresses.includes(tokenAddress.toLowerCase());

          console.log('🔍 [BLESSNET SAFETY] Self-bridging token check:');
          console.log('  - Token address:', tokenAddress);
          console.log('  - Origin chain ID:', originChainId);
          console.log('  - Is self-bridging token?', isSelfBridging);
          console.log('  - Self-bridging addresses from API:', selfBridgingAddresses);
        } catch (error) {
          console.warn('⚠️ [BLESSNET SAFETY] Failed to fetch self-bridging tokens, assuming not self-bridging:', error);
          isSelfBridging = false;
        }
      }

      if (tokenAddress && isSelfBridging) {
        // For self-bridging tokens, check the token's own balance
        addressToCheck = tokenAddress;
        checkReason = `self-bridging token (${tokenAddress})`;
        console.log('🏗️ [BLESSNET SAFETY] Token is self-bridging - checking token address balance');
      } else if (fallbackContractAddress) {
        // For regular tokens, check the gateway contract balance
        addressToCheck = fallbackContractAddress;
        checkReason = `gateway contract (${fallbackContractAddress})`;
        console.log('🚪 [BLESSNET SAFETY] Using gateway contract for balance check');
      } else {
        console.log('⚠️ [BLESSNET SAFETY] No address to check - allowing by default');
        return true;
      }

      console.log('🎯 [BLESSNET SAFETY] Checking balance for:', checkReason);

      const hasSufficientBalance = await hasSufficientBlessBalance(
        addressToCheck,
        BLESSNET_SAFETY_CONFIG.MINIMUM_BLESS_BALANCE_WEI
      );

      if (!hasSufficientBalance) {
        setShowUnavailableModal(true);
        return false;
      }

      return true;
    } catch (error) {
      console.error('❌ [BLESSNET SAFETY] Error validating Blessnet bridging:', error);
      // On error, show the modal as a safety measure
      setShowUnavailableModal(true);
      return false;
    } finally {
      setIsValidating(false);
    }
  }, []);

  /**
   * Checks if the destination is Blessnet
   */
  const isBridgingToBlessnet = useCallback((destinationChain: ChainData | undefined): boolean => {
    return destinationChain?.chainId === BLESSNET_CHAIN_ID;
  }, []);

  /**
   * Closes the unavailable modal
   */
  const closeUnavailableModal = useCallback(() => {
    setShowUnavailableModal(false);
  }, []);

  return {
    validateBlessnetBridging,
    isBridgingToBlessnet,
    isValidating,
    showUnavailableModal,
    closeUnavailableModal,
  };
};
